import { CheckCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import PageContent from '@zeus/core/layout/PageContent';
import AsyncPageContainer from '_/common/components/layout/AsyncPageContainer';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSourceContext } from '_/contexts/SourceContext';
import { useSnapshot, useSnapshotImportStatus, SNAPSHOT_IMPORT_STATUS_QUERY_KEY } from '_/hooks/useSnapshots';
import { Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';

interface StatusRecord {
  name: string;
  startedAt?: string;
  stoppedAt?: string;
  progress?: number;
  error?: string;
  complete: boolean;
}

function ImportStatus(): ReactElement {
  const params = useParams({ strict: false }) as Partial<{ snapshotId: string }>;
  const snapshotId = params.snapshotId || '';
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();
  const queryClient = useQueryClient();

  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProjectSlug, selectedProject } = useProject();
  const { selectedCollectionSlug, selectedCollection } = useCollection();
  const { selectedSourceSlug, selectedSource } = useSourceContext();

  const { data: selectedSnapshot } = useSnapshot(snapshotId);
  const { data: importStatus = [], isLoading: isFetchingStatus, isFetching } = useSnapshotImportStatus(snapshotId);

  const [searchText, setSearchText] = useState('');

  // Format the import status data (convert timestamps to formatted strings)
  const status = importStatus.map((item: any) => ({
    ...item,
    startedAt: item.startedAt ? dayjs(item.startedAt).format('MMM DD, YYYY HH:mm:ss') : '',
    stoppedAt: item.stoppedAt ? dayjs(item.stoppedAt).format('MMM DD, YYYY HH:mm:ss') : '',
  }));

  // Filter status based on search text
  const filteredData = status.filter((record: StatusRecord) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return (
      record.name?.toLowerCase().includes(lowerSearch) ||
      record.startedAt?.toLowerCase().includes(lowerSearch) ||
      record.stoppedAt?.toLowerCase().includes(lowerSearch) ||
      record.error?.toLowerCase().includes(lowerSearch) ||
      record.progress?.toString().includes(lowerSearch)
    );
  });

  const columns: ColumnsType<StatusRecord> = [
    {
      title: 'Stage',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Started at',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 250,
      sorter: (a, b) => {
        if (!a.startedAt) return -1;
        if (!b.startedAt) return 1;
        return dayjs(a.startedAt, 'MMM DD, YYYY HH:mm:ss').unix() - dayjs(b.startedAt, 'MMM DD, YYYY HH:mm:ss').unix();
      },
    },
    {
      title: 'Finished at',
      dataIndex: 'stoppedAt',
      key: 'stoppedAt',
      width: 250,
      sorter: (a, b) => {
        if (!a.stoppedAt) return -1;
        if (!b.stoppedAt) return 1;
        return dayjs(a.stoppedAt, 'MMM DD, YYYY HH:mm:ss').unix() - dayjs(b.stoppedAt, 'MMM DD, YYYY HH:mm:ss').unix();
      },
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      width: 250,
      sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
      render: (progress: number) => (progress !== undefined && progress !== null ? progress : ''),
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      width: 250,
      sorter: (a, b) => (a.error || '').localeCompare(b.error || ''),
    },
    {
      title: 'Completed',
      dataIndex: 'complete',
      key: 'complete',
      width: 120,
      align: 'center',
      filters: [
        { text: 'Completed', value: true },
        { text: 'Not Completed', value: false },
      ],
      onFilter: (value, record) => record.complete === value,
      render: (complete: boolean) => (complete ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null),
    },
  ];

  const onRefreshClick = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: SNAPSHOT_IMPORT_STATUS_QUERY_KEY(snapshotId) });
  }, [queryClient, snapshotId]);

  // Sync React Query fetching state with header refresh loading
  useEffect(() => {
    setRefreshLoading(isFetching);
  }, [isFetching, setRefreshLoading]);

  // Set up breadcrumbs and refresh handler
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
      {
        title: selectedCollection?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      },
      {
        title: 'Sources',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
      },
      {
        title: selectedSource?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
      },
      {
        title: 'Snapshots',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots`,
      },
      {
        title: snapshotId || 'Snapshot',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/home`,
      },
      { title: 'Import Status' },
    ];

    setBreadcrumbs(breadcrumbs);
    setOnRefresh(onRefreshClick);

    return () => {
      setBreadcrumbs([]);
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [
    selectedOrganization?.name,
    selectedOrgSlug,
    selectedProject?.name,
    selectedProjectSlug,
    selectedCollection?.name,
    selectedCollectionSlug,
    selectedSource?.name,
    selectedSourceSlug,
    snapshotId,
    setBreadcrumbs,
    setOnRefresh,
    setRefreshLoading,
    onRefreshClick,
  ]);

  return (
    <AsyncPageContainer isLoading={!selectedSnapshot || isFetchingStatus}>
      <>
        <PageContent>
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search by stage, date, error, or progress..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ maxWidth: 400 }}
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => `${record.name}-${record.startedAt || 'pending'}`}
            loading={isFetchingStatus}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </PageContent>
      </>
    </AsyncPageContainer>
  );
}

export default ImportStatus;
